/**
 * 머지 직후 속도 안정화기.
 *
 * 두 행성이 합쳐져 더 큰 행성이 미드포인트에 spawn 되면, cannon-es 가
 * "큰 새 몸체가 주변과 겹쳐 있음" 을 풀기 위해 강한 분리 impulse 를
 * 적용한다. 이게 화면에서는 "위로 튐(jump)" 으로 보인다. tier 가 높을
 * 수록 새 반경이 크고 → 겹침이 많고 → 점프가 과도해짐.
 *
 * 이 유틸은 머지 spawn 시점에 행성 id 를 등록하고, 일정 시간(약 250ms)
 * 동안 매 프레임 그 행성의 속도를 tier-aware 상한으로 clamp 한다.
 * tier 가 높을수록 약간 더 큰 상한이 허용 (사용자 의도: "행성 크기마다
 * 점프 반경이 넓어지는건 좋은데 적당히") — 단, 상한은 충분히 낮아 폭주
 * 점프는 막힌다. 윈도우가 끝나면 자동으로 해제되고 일반 물리로 복귀.
 *
 * Pure logic — body 인터페이스만 알고 있어 단위 테스트 가능.
 */

export interface SettleBody {
  velocity: { x: number; y: number; z: number };
}

export interface SettleEntry {
  id: number;
  body: SettleBody;
  tierIndex: number;
  /** 윈도우 만료 시각 (ms, performance.now() 기준). */
  expiresAt: number;
}

export interface MergeSettle {
  /** 머지로 갓 생긴 행성 등록. 동일 id 재등록 시 덮어쓴다. */
  register(id: number, body: SettleBody, tierIndex: number, nowMs: number): void;
  /** 매 프레임 호출 — 등록된 행성의 속도를 clamp + 만료 정리. */
  tick(nowMs: number): void;
  /** 활성 항목 수 (디버그/테스트용). */
  activeCount(): number;
  /** 런 재시작 시 호출. */
  reset(): void;
}

export interface MergeSettleOptions {
  /** Settle window in ms. Default 250. */
  windowMs?: number;
  /**
   * Cap velocity magnitude per tier. Default: 2.5 + tier * 0.45 →
   * tier 0 ≈ 2.5, tier 8 ≈ 6.1. Bigger tiers keep a richer "bounce
   * radius" while the cap prevents runaway upward springs.
   */
  velocityCapForTier?: (tierIndex: number) => number;
}

const DEFAULT_WINDOW_MS = 250;
const DEFAULT_CAP = (tierIndex: number): number => 2.5 + tierIndex * 0.45;

export function createMergeSettle(
  opts: MergeSettleOptions = {},
): MergeSettle {
  const windowMs = opts.windowMs ?? DEFAULT_WINDOW_MS;
  const capFor = opts.velocityCapForTier ?? DEFAULT_CAP;
  const entries = new Map<number, SettleEntry>();

  const register = (
    id: number,
    body: SettleBody,
    tierIndex: number,
    nowMs: number,
  ): void => {
    entries.set(id, { id, body, tierIndex, expiresAt: nowMs + windowMs });
  };

  const tick = (nowMs: number): void => {
    for (const [id, entry] of entries) {
      if (nowMs >= entry.expiresAt) {
        entries.delete(id);
        continue;
      }
      const v = entry.body.velocity;
      const speed = Math.hypot(v.x, v.y, v.z);
      const cap = capFor(entry.tierIndex);
      if (speed > cap && speed > 0) {
        const k = cap / speed;
        v.x *= k;
        v.y *= k;
        v.z *= k;
      }
    }
  };

  const activeCount = (): number => entries.size;

  const reset = (): void => {
    entries.clear();
  };

  return { register, tick, activeCount, reset };
}

export const __testing = { DEFAULT_WINDOW_MS, DEFAULT_CAP };
